// Login.jsx - Complete Fix
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography, Snackbar, Alert, Button } from "@mui/material";
import "./Login.css";
import { useUser } from "./UserContextPersistent";
import HomeHeader from "../../components/header/homeHeader/HomeHeader";
import StandardLoginForm from "./StandardLoginForm";
import PhoneInput from "./PhoneInput";
import ForgotPasswordForm from "./ForgotPasswordForm";
import SocialLogin from "./SocialLogin";
import RegistrationPrompt from "./RegistrationPrompt";
import OtpVerification from "./OtpVerification";
import { otp } from "../../config/Pageitems";
import LoginLeftContainer from "./LoginLeftContainer";
import Footer from "../../components/common/Footer";
import { registerPushNotifications } from "../../utils/pushNotifications";
import { useLayoutContext } from "../../context/LayoutContext";
import { getPageIdForSelectedApp } from "../../config/uiLayoutConfig";
import config from "../../config.js";
import { getDefaultApplicationTarget, getDefaultApplicationValue } from "../../utils/defaultApplication";
import { redirectToTenantHost } from "../../utils/tenantAuthHandoff";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch, updateDemo, state } = useUser();
  const { preloadLayout } = useLayoutContext();
  const navigationTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  // Clear previously selected app state on login page load
  useEffect(() => {
    localStorage.removeItem("selectedApp");
  }, []);

  // Message state
  const [notification, setNotification] = useState({
    message: "",
    type: "success",
    open: false,
  });
  const [loading, setLoading] = useState(false);

  // View state
  const [showOtpFlow, setShowOtpFlow] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Auth state
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  // Generic notification handler
  const showNotification = (message, type = "success") => {
    if (!mountedRef.current) return;

    setNotification({
      message,
      type,
      open: true,
    });
  };

  // Reset authentication state
  const resetAuthState = () => {
    sessionStorage.removeItem("authToken");
    const cookies = new (require("universal-cookie"))();
    cookies.remove("token", { path: "/" });
    setConfirmationResult(null);
    dispatch({ type: "CLEAR_USER" });
  };

  // Handle navigating back to login form
  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setShowOtpFlow(false);
    setShowOtpInput(false);
  };

  // Handle OTP workflow navigation
  const handleOTPLogin = () => {
    setShowOtpFlow(true);
    setShowForgotPassword(false);
    setShowOtpInput(false);
    resetAuthState();
  };

  // Handle forgot password navigation
  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setShowOtpFlow(false);
    setShowOtpInput(false);
  };

  const handleLoginSuccess = async (
    message = "Login successful!",
    userData = null,
  ) => {
    if (
      message === "Phone verification successful!" ||
      message === "Login successful!" ||
      message === "Google login successful!"
    ) {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      console.log("=== LOGIN SUCCESS DEBUG ===");
      console.log("userData received:", userData);
      console.log("state.organization:", state.organization);

      let storedOrg = null;
      try {
        const userState = JSON.parse(localStorage.getItem("userState") || "{}");
        storedOrg = userState?.organization || null;
      } catch (e) {
        console.error("Error parsing userState:", e);
      }
      if (!storedOrg) {
        try {
          const rawOrg = localStorage.getItem("organization");
          storedOrg = rawOrg ? JSON.parse(rawOrg) : null;
        } catch (e) {
          console.error("Error parsing organization:", e);
        }
      }

      const orgData = {
        ...(storedOrg || {}),
        ...(state.organization || {}),
        ...(userData?.organization || {}),
      };

      console.log("Final orgData:", orgData);
      console.log("Final orgData.organizationType:", orgData?.organizationType);
      console.log("Final orgData.appHostname:", orgData?.appHostname);

      const orgTypes = Array.isArray(orgData?.organizationType)
        ? orgData.organizationType
        : orgData?.organizationType
          ? [orgData.organizationType]
          : [];
      const isStaffOnly =
        orgTypes.includes("staffTrack") &&
        !orgTypes.includes("vehicleTrack") &&
        !orgTypes.includes("schoolTrack");

      if (!isStaffOnly) {
        const redirectedToTenant = redirectToTenantHost({
          organization: orgData,
          user: userData || state.user,
          features: state.features || [],
        });
        if (redirectedToTenant) {
          return;
        }
      }

      try {
        await registerPushNotifications(userData?.id);
      } catch {
        /* logged in pushNotifications */
      }

      const getSavedRedirectUrl = () => {
        try {
          const searchParams = new URLSearchParams(window.location.search);
          const queryRedirect = searchParams.get("redirect");
          if (queryRedirect && queryRedirect !== "/" && queryRedirect !== "/login") {
            return decodeURIComponent(queryRedirect);
          }
        } catch (e) {
          console.error("Error reading query redirect:", e);
        }
        const sessionRedirect = sessionStorage.getItem("redirectUrl");
        if (sessionRedirect && sessionRedirect !== "/" && sessionRedirect !== "/login") {
          return sessionRedirect;
        }
        const localRedirect = localStorage.getItem("redirectUrl");
        if (localRedirect && localRedirect !== "/" && localRedirect !== "/login") {
          return localRedirect;
        }
        if (location.state?.from) {
          const fromPath = location.state.from.pathname + (location.state.from.search || "");
          if (fromPath !== "/" && fromPath !== "/login") {
            return fromPath;
          }
        }
        return null;
      };

      const savedRedirectUrl = getSavedRedirectUrl();

      if (savedRedirectUrl) {
        sessionStorage.removeItem("redirectUrl");
        localStorage.removeItem("redirectUrl");
        localStorage.setItem("selectedApp", "vehicleTrack");
        navigationTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            navigate(savedRedirectUrl);
          }
        }, 300);
        return;
      }

      if (orgData?.organizationType) {
        const orgTypes = Array.isArray(orgData.organizationType)
          ? orgData.organizationType
          : [orgData.organizationType];

        console.log("Organization types:", orgTypes);
        const appCount = orgTypes.length;
        console.log("App count:", appCount);

        const defaultApplication = await resolveDefaultApplication(orgData);
        console.log("Resolved default application:", defaultApplication);

        if (appCount > 1 && !defaultApplication) {
          navigationTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              navigate("/app-select");
            }
          }, 300);
          return;
        }

        navigationTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            navigateToApp(orgData, defaultApplication);
          }
        }, 300);
      } else {
        console.log("No organizationType found, going to dashboard");
        navigate("/dashboard");
      }
    }
  };

  const resolveDefaultApplication = async (orgData) => {
    const organizationId = state?.user?.organizationId || orgData?.id;

    try {
      const savedSettings = JSON.parse(localStorage.getItem("appSettings") || "{}") || {};
      const savedDefaultApplication = getDefaultApplicationValue(savedSettings);
      if (savedDefaultApplication) {
        return savedDefaultApplication;
      }
    } catch (error) {
      console.error("Error reading saved app settings:", error);
    }

    if (!organizationId) {
      return null;
    }

    try {
      const response = await axios.get(
        `${config.baseURL}/appSettings/organization?organizationId=${organizationId}`,
        { withCredentials: true }
      );
      const fetchedSettings = response.data || {};
      const fetchedDefaultApplication = getDefaultApplicationValue(fetchedSettings);
      if (fetchedDefaultApplication) {
        localStorage.setItem("appSettings", JSON.stringify(fetchedSettings));
      }
      return fetchedDefaultApplication;
    } catch (error) {
      console.error("Error fetching app settings for login redirect:", error);
      return null;
    }
  };

  const navigateToApp = (orgData, preferredDefaultApplication = null) => {
    console.log("Navigating to app with orgData:", orgData);
    const organizationId = state?.user?.organizationId || orgData?.id;
    let selectedApp = null;
    let target = "/dashboard";

    const queryRedirect = new URLSearchParams(window.location.search).get("redirect");
    const savedRedirectUrl =
      (queryRedirect ? decodeURIComponent(queryRedirect) : null) ||
      sessionStorage.getItem("redirectUrl") ||
      localStorage.getItem("redirectUrl") ||
      (location.state?.from ? (location.state.from.pathname + (location.state.from.search || "")) : null);

    const savedSettings = JSON.parse(localStorage.getItem("appSettings") || "{}") || {};
    const defaultApplication = preferredDefaultApplication || getDefaultApplicationValue(savedSettings);

    if (orgData?.organizationType && Array.isArray(orgData.organizationType)) {
      const orgTypes = orgData.organizationType;
      const defaultTarget = getDefaultApplicationTarget(defaultApplication);

      if (defaultTarget && orgTypes.includes(defaultTarget.id)) {
        selectedApp = defaultTarget.id;
        target = defaultTarget.route;
      } else if (orgTypes.includes("vehicleTrack")) {
        selectedApp = "vehicleTrack";
        target = "/dashboard";
      } else if (orgTypes.includes("schoolTrack")) {
        selectedApp = "schoolTrack";
        target = "/school";
      } else if (orgTypes.includes("staffTrack")) {
        selectedApp = "staffTrack";
        localStorage.setItem("selectedApp", selectedApp);
        window.location.href = "https://staff.routeeye.io";
        return;
      } else if (orgTypes.includes("custom")) {
        selectedApp = "custom";
        target = "/dashboard";
      }
    }

    if (selectedApp) {
      localStorage.setItem("selectedApp", selectedApp);
      const pageId = getPageIdForSelectedApp(selectedApp);
      if (organizationId && pageId) {
        preloadLayout(pageId, organizationId).catch(() => { });
      }
    }

    if (savedRedirectUrl && savedRedirectUrl !== "/" && savedRedirectUrl !== "/login") {
      sessionStorage.removeItem("redirectUrl");
      localStorage.removeItem("redirectUrl");
      console.log("Redirecting user to saved URL after login:", savedRedirectUrl);
      navigate(savedRedirectUrl);
    } else {
      navigate(target);
    }
  };

  const handleLoginError = (errorMessage) => {
    showNotification(errorMessage, "error");
  };

  return (
    <>
      <HomeHeader />
      <div className="login_container">
        <div className="content_container">
          <LoginLeftContainer />

          <div
            className="container_container2"
            style={{
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
            }}
          >
            {showOtpFlow ? (
              showOtpInput ? (
                <OtpVerification
                  phoneNumber={phoneNumber}
                  confirmationResult={confirmationResult}
                  loading={loading}
                  setLoading={setLoading}
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginError}
                  handleBackToLogin={handleBackToLogin}
                  dispatch={dispatch}
                  navigate={navigate}
                  resetAuthState={resetAuthState}
                />
              ) : (
                <PhoneInput
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  loading={loading}
                  setLoading={setLoading}
                  setShowOtpInput={setShowOtpInput}
                  setConfirmationResult={setConfirmationResult}
                  setSuccessMessage={(msg) => showNotification(msg)}
                  setErrorMessage={(msg) => showNotification(msg, "error")}
                  setOpen={() => { }}
                  handleBackToLogin={handleBackToLogin}
                />
              )
            ) : showForgotPassword ? (
              <ForgotPasswordForm
                loading={loading}
                setLoading={setLoading}
                handleBackToLogin={handleBackToLogin}
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
              />
            ) : (
              <>
                <StandardLoginForm
                  loading={loading}
                  setLoading={setLoading}
                  handleOTPLogin={handleOTPLogin}
                  handleForgotPassword={handleForgotPassword}
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginError}
                  dispatch={dispatch}
                  navigate={navigate}
                  updateDemo={updateDemo}
                />
                <SocialLogin
                  loading={loading}
                  setLoading={setLoading}
                  onSuccess={handleLoginSuccess}
                  onError={handleLoginError}
                  dispatch={dispatch}
                  navigate={navigate}
                />
                <RegistrationPrompt navigate={navigate} />
              </>
            )}
          </div>
        </div>
      </div>
      <Footer isLogin={true} />

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => {
          if (mountedRef.current) {
            setNotification((prev) => ({ ...prev, open: false }));
          }
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ marginTop: "70px" }}
      >
        <Alert
          onClose={() => {
            if (mountedRef.current) {
              setNotification((prev) => ({ ...prev, open: false }));
            }
          }}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
